<template>
  <div id="feasibilityResultModal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header align-items-start">
          <div>
            <div class="d-flex align-items-center gap-2 mb-1">
              <span class="fz-pill" :class="pill.css">{{ pill.label }}</span>
              <span class="fz-muted small">Check #{{ requestId }}</span>
            </div>
            <h5 class="modal-title mb-0">{{ title }}</h5>
            <div class="small fz-muted">{{ detailLine }}</div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <div v-if="loading && !check" class="text-center fz-muted py-5">
            <span class="spinner-border spinner-border-sm me-2"></span>Loading…
          </div>

          <!-- still running -->
          <div v-else-if="check && isActive(check)" class="text-center py-5">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <div class="fw-semibold">{{ check.status === 'PENDING' ? 'Queued' : 'Running' }}</div>
            <div class="fz-muted small">
              Started {{ formatDateTime(check.postedAt) }} · {{ duration(check.postedAt, null, now) }} so far.
              This window updates on its own.
            </div>
          </div>

          <div v-else-if="check && check.status === 'ERROR'" class="alert alert-danger">
            <div class="fw-semibold mb-1">The check failed</div>
            {{ check.errorMessage || 'No error message was recorded.' }}
          </div>

          <template v-else-if="result">
            <p class="mb-3 fz-muted" v-if="verdictHint">{{ verdictHint }}</p>

            <!-- headline numbers -->
            <div class="row g-3 mb-3">
              <div class="col-6 col-lg">
                <div class="fz-tile">
                  <div class="fz-tile-label">Exposed devices</div>
                  <div class="fz-tile-value">{{ formatNumber(headline.exposedDevices != null ? headline.exposedDevices : (result.projected && result.projected.exposedKeys)) }}</div>
                  <div class="fz-tile-note">projected over the flight</div>
                </div>
              </div>
              <div class="col-6 col-lg">
                <div class="fz-tile">
                  <div class="fz-tile-label">{{ isWeb ? 'Devices firing the pixel' : 'Devices inside the location' }}</div>
                  <div class="fz-tile-value">{{ formatNumber(headline.targetDevices) }}</div>
                  <div class="fz-tile-note">{{ isWeb && headline.method === 'RATE' ? "in the pixel's live window" : 'in the baseline window' }}</div>
                </div>
              </div>
              <div class="col-6 col-lg">
                <div class="fz-tile" :class="gateClass(headline.exposedConverters, gate.exposedConverters)">
                  <div class="fz-tile-label">Exposed converters</div>
                  <div class="fz-tile-value">{{ formatNumber(headline.exposedConverters, 1) }}</div>
                  <div class="fz-tile-note">needs ≥ {{ gate.exposedConverters }}</div>
                </div>
              </div>
              <div class="col-6 col-lg">
                <div class="fz-tile" :class="gateClass(headline.controlConvertersPerDraw, gate.controlConvertersPerDraw)">
                  <div class="fz-tile-label">Control converters / draw</div>
                  <div class="fz-tile-value">{{ formatNumber(headline.controlConvertersPerDraw, 1) }}</div>
                  <div class="fz-tile-note">needs ≥ {{ gate.controlConvertersPerDraw }}</div>
                </div>
              </div>
              <div class="col-12 col-lg">
                <div class="fz-tile">
                  <div class="fz-tile-label">Expected 95% CI</div>
                  <div class="fz-tile-value">{{ headlineCi != null ? '± ' + formatPercent(headlineCi) : '—' }}</div>
                  <div class="fz-tile-note">on the lift, for information</div>
                </div>
              </div>
            </div>

            <!-- reasons + warnings -->
            <div class="row g-3 mb-3">
              <div class="col-12 col-lg-6">
                <div class="fz-section-title">Why</div>
                <ul class="mb-0 ps-3">
                  <li v-for="(reason, i) in (result.reasons || [])" :key="'r' + i" class="mb-1">{{ reason }}</li>
                </ul>
              </div>
              <div class="col-12 col-lg-6" v-if="(result.warnings || []).length">
                <div class="fz-section-title">Worth knowing</div>
                <div v-for="(warning, i) in result.warnings" :key="'w' + i" class="fz-warning mb-2">
                  <font-awesome-icon :icon="['fas', 'circle-info']" class="me-2 fz-muted" />{{ warning }}
                </div>
              </div>
            </div>

            <!-- how it was measured -->
            <div class="fz-facts mb-3">
              <div>
                <div class="fz-tile-label">Flight</div>
                <div>{{ result.flight && result.flight.start }} – {{ result.flight && result.flight.end }} ({{ result.flight && result.flight.days }} d)</div>
              </div>
              <div v-if="result.baseline">
                <div class="fz-tile-label">Baseline window</div>
                <div>{{ result.baseline.start }} – {{ result.baseline.end }} ({{ result.baseline.days }} d)</div>
                <div v-if="result.baseline.lookbackDays" class="small fz-muted">moved back {{ result.baseline.lookbackDays }} days to find matched inventory</div>
              </div>
              <div v-if="result.inventory">
                <div class="fz-tile-label">Units with matched devices</div>
                <div>{{ result.inventory.unitsCovered }} of {{ result.inventory.units }} ({{ formatPercent(result.inventory.unitCoverage) }})</div>
              </div>
              <div>
                <div class="fz-tile-label">Requested</div>
                <div>{{ formatDateTime(check.postedAt) }}<span v-if="check.requestedBy"> · {{ check.requestedBy }}</span></div>
              </div>
              <div v-if="runSeconds(check) !== null">
                <div class="fz-tile-label">Run time</div>
                <div>{{ formatSeconds(runSeconds(check)) }}<span class="fz-muted" v-if="queueSeconds"> (+{{ formatSeconds(queueSeconds) }} queued)</span></div>
                <div class="small fz-muted" :title="'Estimate from run time on the Large warehouse; not a billed figure.'">{{ formatCost(runSeconds(check)) }} (est.)</div>
              </div>
            </div>

            <!-- per event -->
            <div v-if="(result.events || []).length > 1 || isWeb">
              <div class="fz-section-title">{{ isWeb ? 'By pixel' : 'Detail' }}</div>
              <div class="table-responsive">
                <table class="table table-sm align-middle fz-table mb-1">
                  <thead>
                  <tr>
                    <th>{{ isWeb ? 'Pixel' : 'Conversion' }}</th>
                    <th>Verdict</th>
                    <th class="text-end">Exposed devices</th>
                    <th class="text-end">{{ isWeb ? 'Firing the pixel' : 'Inside the location' }}</th>
                    <th class="text-end">Exposed conv.</th>
                    <th class="text-end">Control conv. / draw</th>
                    <th class="text-end">95% CI</th>
                    <th>Estimate</th>
                  </tr>
                  </thead>
                  <tbody>
                  <template v-for="event in result.events" :key="event.event">
                    <tr>
                      <td class="fw-semibold">{{ event.event === '__ALL__' ? 'Store visits' : event.event }}</td>
                      <td><span class="fz-pill fz-pill-sm" :class="verdictMeta(event.verdict).css">{{ verdictMeta(event.verdict).label }}</span></td>
                      <td class="text-end">{{ formatNumber(event.exposedDevices) }}</td>
                      <td class="text-end">{{ formatNumber(event.targetDevices) }}</td>
                      <td class="text-end">{{ formatNumber(event.exposedConverters, 1) }}</td>
                      <td class="text-end">{{ formatNumber(event.controlConvertersPerDraw, 1) }}</td>
                      <td class="text-end">{{ event.ci95HalfWidth != null ? '± ' + formatPercent(event.ci95HalfWidth) : '—' }}</td>
                      <td class="small fz-muted">{{ methodLabel(event.method) }}</td>
                    </tr>
                    <tr class="fz-reason-row">
                      <td colspan="8" class="small fz-muted">{{ (event.reasons || []).join(' ') }}</td>
                    </tr>
                  </template>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <span class="me-auto small fz-muted">
            Counts are projected to the full flight from a window before it. The CI is for information only.
          </span>
          <button type="button" class="btn btn-light border" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global bootstrap */
import AttributionService from '../services/AttributionService';
import {
  VERDICTS, duration, formatCost, formatDateTime, formatNumber, formatPercent, formatSeconds, isActive, parseTimestamp, pillFor,
  runSeconds, subjectDetail, subjectTitle, unwrap,
} from '../utils/feasibility';

const POLL_MS = 10000;

export default {
  name: 'FeasibilityResultModal',
  props: {
    requestId: { type: Number, required: true },
    summary: { type: Object, default: null },
  },
  emits: ['closed', 'updated'],
  data() {
    return { check: null, loading: false, timer: null, now: Date.now(), modalElement: null, currentModal: null };
  },
  computed: {
    result() {
      return this.check && this.check.result;
    },
    pill() {
      return pillFor(this.check ? { ...this.check, verdict: this.result && this.result.verdict } : this.summary);
    },
    isWeb() {
      return (this.result && this.result.studyType) === 'WEB';
    },
    gate() {
      return (this.result && this.result.gate) || { exposedConverters: 2, controlConvertersPerDraw: 5 };
    },
    headline() {
      const events = (this.result && this.result.events) || [];
      return events.find((event) => event.event === this.result.headlineEvent) || events[0] || {};
    },
    headlineCi() {
      if (this.headline.ci95HalfWidth != null) return this.headline.ci95HalfWidth;
      return this.result && this.result.precision ? this.result.precision.ci95HalfWidthAtZero : null;
    },
    /** Seconds between being requested and picked up by the task */
    queueSeconds() {
      const posted = parseTimestamp(this.check && this.check.postedAt);
      const started = parseTimestamp(this.check && this.check.startedAt);
      if (!posted || !started) return 0;
      return Math.max(0, Math.round((started.getTime() - posted.getTime()) / 1000));
    },
    verdictHint() {
      return this.result && VERDICTS[this.result.verdict] ? VERDICTS[this.result.verdict].hint : '';
    },
    title() {
      return this.summary ? subjectTitle(this.summary) : `Check #${this.requestId}`;
    },
    detailLine() {
      if (!this.summary) return '';
      return [this.summary.customerName, subjectDetail(this.summary)].filter(Boolean).join(' · ');
    },
  },
  mounted() {
    this.modalElement = document.getElementById('feasibilityResultModal');
    this.currentModal = new bootstrap.Modal(this.modalElement);
    this.modalElement.addEventListener('hidden.bs.modal', this.onModalHidden);
    this.currentModal.show();
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
    if (this.modalElement) this.modalElement.removeEventListener('hidden.bs.modal', this.onModalHidden);
  },
  methods: {
    isActive, formatNumber, formatPercent, formatDateTime, duration, formatCost, formatSeconds, runSeconds,
    async load() {
      this.loading = true;
      this.now = Date.now();
      try {
        this.check = unwrap(await new AttributionService().getFeasibility(this.requestId));
        this.$emit('updated', this.check);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
      if (!this.check || isActive(this.check)) this.timer = setTimeout(this.load, POLL_MS);
    },
    verdictMeta(verdict) {
      return VERDICTS[verdict] || { label: verdict || '—', css: 'fz-pill-muted' };
    },
    methodLabel(method) {
      return { DIRECT: 'Counted in the baseline', RATE: "From the pixel's own rate", NONE: 'No pixel data' }[method] || '';
    },
    gateClass(value, threshold) {
      if (value === null || value === undefined) return '';
      return Number(value) >= Number(threshold) ? 'fz-tile-ok' : 'fz-tile-short';
    },
    onModalHidden() {
      this.$emit('closed');
    },
  },
};
</script>

<style scoped>
.fz-tile {
  border: 1px solid #e3e8ed;
  border-radius: 10px;
  padding: 12px 14px;
  height: 100%;
  background: #fff;
}
.fz-tile-ok {
  border-color: #b7dfc4;
  background: #f3faf5;
}
.fz-tile-short {
  border-color: #f1c9c4;
  background: #fdf5f4;
}
.fz-tile-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7a87;
}
.fz-tile-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #052f49;
  line-height: 1.3;
}
.fz-tile-note {
  font-size: 0.8rem;
  color: #6b7a87;
}
.fz-section-title {
  font-weight: 600;
  color: #052f49;
  margin-bottom: 6px;
}
.fz-warning {
  background: #f6f8fa;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.9rem;
}
.fz-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px 24px;
  background: #f6f8fa;
  border-radius: 10px;
  padding: 12px 16px;
}
.fz-table th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7a87;
  font-weight: 600;
}
.fz-reason-row td {
  border-top: 0;
  padding-top: 0;
  padding-bottom: 10px;
}
</style>
